import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useRecoilValue } from 'recoil';

import { Theme } from '@constants';
import { dayDateTimeState } from '@store';

import { MapLegendBackground, MapLegendSizeClass } from './MapLegendBackground';
import { OverlayType } from './MapTypes';

import { TimeUtils } from '../../helpers/TimeUtils';
import { TWCProgressBar } from '../primitives/TWCProgressBar/TWCProgressBar';
import { TWCText } from '../primitives/TWCText/TWCText';

export { MapLegendSizeClass };

const tPath = 'map.legend.';
const loadingTPath = 'map.loading.';
const loadingHorizontalPadding = 10;

// Provide the X position and width for time plotting within each legend type.  Time content should be centered in that area.
const timePositions = {
  [OverlayType.alerts]: {
    x: 0,
    width: 0,
  },
  [OverlayType.breathingIndex]: {
    x: 363,
    width: 187,
  },
  [OverlayType.feelsLike]: {
    x: 273,
    width: 256,
  },
  [OverlayType.futureRadar]: {
    x: 329,
    width: 204,
  },
  [OverlayType.lightning]: {
    x: 273,
    width: 204,
  },
  [OverlayType.radar]: {
    [MapLegendSizeClass.regular]: {
      x: 273,
      width: 204,
    },
    [MapLegendSizeClass.short]: {
      x: 142,
      width: 146,
    },
  },
  [OverlayType.satellite]: {
    x: 253,
    width: 204,
  },
  [OverlayType.temperature]: {
    x: 314,
    width: 215,
  },
  [OverlayType.wind]: {
    x: 213,
    width: 135,
  },
};

const MapLegendDayTimeTZText = ({ dateObject }) => {
  if (dateObject === null) {
    return null;
  }

  return (
    <TWCText noTestID accessible={false} variant={'regularText20'}>
      {[
        TimeUtils.dayOfWeekAbbr(dateObject),
        TimeUtils.getLocalTime(dateObject, TimeUtils.getTimeFormat()),
        TimeUtils.localTimeZoneAbbreviation(),
      ].join(' ')}
    </TWCText>
  );
};

// Show the time box, which typically has a progress bar and/or a time display for the frame shown.
const MapLegendTimeBox = ({ animationLoading, overlayType, sizeClass, time, currentFrame, maxFrames }) => {
  const { t } = useTranslation();
  const { timeZone } = useRecoilValue(dayDateTimeState);

  const position = timePositions[overlayType][sizeClass] ?? timePositions[overlayType];

  if (position.width < 1) {
    return null;
  }

  const dateObject = time ? TimeUtils.dateFromEpoch(time) : null;
  let timeBoxContent = [];

  if (animationLoading) {
    timeBoxContent = (
      <>
        <TWCProgressBar
          currentIncrement={currentFrame}
          totalIncrements={maxFrames}
          width={Theme.px(position.width - loadingHorizontalPadding * 2)}
        />
        <View style={styles.timeProgressAnnotations}>
          <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
            {t(`${loadingTPath}body`)}
          </TWCText>
        </View>
      </>
    );
  } else {
    switch (overlayType) {
      case OverlayType.futureRadar:
      case OverlayType.radar:
      case OverlayType.satellite:
        // Progress bar, day of week, and time of day.
        timeBoxContent = (
          <>
            <TWCProgressBar
              currentIncrement={currentFrame}
              totalIncrements={maxFrames}
              width={Theme.px(position.width - loadingHorizontalPadding * 2)}
            />
            <View style={styles.timeProgressAnnotations}>
              <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
                {dateObject ? TimeUtils.dayOfWeekAbbr(dateObject) : ''}
              </TWCText>
              <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
                {dateObject ? TimeUtils.getZonedTime(dateObject, timeZone, TimeUtils.getTimeFormat()) : ''}
              </TWCText>
            </View>
          </>
        );
        break;

      case OverlayType.lightning:
        // Progress bar, "Past 15 minutes", and time of day.
        timeBoxContent = (
          <>
            <TWCProgressBar
              currentIncrement={currentFrame}
              totalIncrements={maxFrames}
              width={Theme.px(position.width - loadingHorizontalPadding * 2)}
            />
            <View style={styles.timeProgressAnnotations}>
              <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
                {t(`${tPath}lightningTimeLabel`)}
              </TWCText>
              <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
                {dateObject ? TimeUtils.getZonedTime(dateObject, timeZone, TimeUtils.getTimeFormat()) : ''}
              </TWCText>
            </View>
          </>
        );
        break;

      case OverlayType.feelsLike:
      case OverlayType.temperature:
      case OverlayType.wind:
        // String with day of week, time (a/p), time zone initials.
        timeBoxContent = <MapLegendDayTimeTZText dateObject={TimeUtils.dateFromEpoch(time)} />;
        break;

      case OverlayType.breathingIndex:
        // Upcoming day string (Tomorrow, Wednesday, etc).
        timeBoxContent = (
          <TWCText noTestID importantForAccessibility={'no'} variant={'regularText20'}>
            <TimeUtils.RelativeDayOrNight date={TimeUtils.dateFromEpoch(time)} />
          </TWCText>
        );
        break;

      case OverlayType.alerts:
        // No time box.
        break;
    }
  }

  return (
    <View
      style={[
        styles.timeBoxContainer,
        {
          left: Theme.px(position.x),
          width: Theme.px(position.width),
        },
      ]}
    >
      {timeBoxContent}
    </View>
  );
};

// MapLegendTimeBox.propTypes = {
//   animationLoading: PropTypes.bool,
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   sizeClass: PropTypes.oneOf(Object.values(MapLegendSizeClass)),
//   time: PropTypes.number, // time of frame, in seconds since epoch
//   currentFrame: PropTypes.number.isRequired,
//   maxFrames: PropTypes.number.isRequired,
// };

MapLegendTimeBox.defaultProps = {
  animationLoading: false,
  sizeClass: Object.values(MapLegendSizeClass),
  time: null,
};

// Show a map legend for a specified OverlayType.
export const MapLegend = forwardRef(({ animationLoading, overlayType, sizeClass }, reference) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(true);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [maxFrames, setMaxFrames] = useState(1);
  const [time, setTime] = useState(0);

  const overlayPath = 'map.overlayTitle.';
  const accessibilityOverlayTitle = t(`${overlayPath}${overlayType}`);
  const accessibilityExtension = t(`${tPath}appendedAccessibilityLabel`);

  useImperativeHandle(
    reference,
    () => ({
      updateLegendState,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentFrame, maxFrames, time],
  );

  const updateLegendState = (newVisible, newCurrentFrame, newMaxFrames, newTime) => {
    setVisible(newVisible);

    if (currentFrame !== newCurrentFrame) {
      setCurrentFrame(newCurrentFrame);
    }
    if (maxFrames !== newMaxFrames) {
      setMaxFrames(newMaxFrames);
    }
    if (time !== newTime) {
      setTime(newTime);
    }
  };

  return (
    <View
      accessibilityHint={''}
      accessibilityLabel={`${accessibilityOverlayTitle} ${accessibilityExtension}`}
      style={!visible && styles.displayNone}
    >
      <MapLegendBackground overlayType={overlayType} sizeClass={sizeClass} />
      <MapLegendTimeBox
        animationLoading={animationLoading}
        currentFrame={currentFrame}
        maxFrames={maxFrames}
        overlayType={overlayType}
        sizeClass={sizeClass}
        time={time}
      />
    </View>
  );
});

// MapLegend.propTypes = {
//   animationLoading: PropTypes.bool,
//   overlayType: PropTypes.oneOf(Object.values(OverlayType)).isRequired,
//   sizeClass: PropTypes.oneOf(Object.values(MapLegendSizeClass)),
// };

MapLegend.defaultProps = {
  animationLoading: false,
  sizeClass: MapLegendSizeClass.regular,
};

const styles = StyleSheet.create({
  timeBoxContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
  },
  timeProgressAnnotations: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: Theme.px(loadingHorizontalPadding),
    marginTop: Theme.px(8),
  },
  displayNone: {
    display: 'none',
  },
});
